import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { apiClient } from '../services/api'
import { isHtmlContent } from '../utils/articleUtils'

interface SharedArticle {
  title: string
  content: string | null
  expiresAt: string
}

export default function SharedArticlePage() {
  const { token } = useParams<{ token: string }>()
  const [article, setArticle] = useState<SharedArticle | null>(null)
  const [expired, setExpired] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) {
      setExpired(true)
      setLoading(false)
      return
    }
    apiClient
      .getSharedArticle(token)
      .then(setArticle)
      .catch((err: unknown) => {
        // Treat all errors (404, 410 LINK_EXPIRED, or unexpected) as expired.
        // Recipients don't benefit from an error distinction here, and logging
        // unexpected errors helps us catch server-side issues during debugging.
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status !== 404 && status !== 410) {
          console.error('Unexpected error fetching shared article:', err)
        }
        setExpired(true)
      })
      .finally(() => setLoading(false))
  }, [token])

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    )
  }

  if (expired || !article) {
    return (
      <div className="shared-expired-page">
        <div className="shared-expired-card">
          <div className="shared-expired-logo">
            <img src="/favicon.svg" alt="Eizel" width={28} height={28} />
            <span>Eizel</span>
          </div>
          <h1 className="shared-expired-heading">This link has expired</h1>
          <p className="shared-expired-body">
            Someone shared this article with you through Eizel — a personal read-later app for
            articles and newsletters.
          </p>
          <a href="https://eizel.co" className="shared-expired-cta">
            Sign up at eizel.co →
          </a>
        </div>
      </div>
    )
  }

  return (
    <>
      <header className="shared-article-header">
        <a href="https://eizel.co" className="shared-article-logo">
          <img src="/favicon.svg" alt="Eizel" width={22} height={22} />
          <span>Eizel</span>
        </a>
      </header>
      <main className="page">
        <h1 className="article-detail-title">{article.title}</h1>
        <hr className="article-detail-divider" />
        {article.content ? (
          isHtmlContent(article.content) ? (
            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            />
          ) : (
            <div className="article-content article-content--plaintext">{article.content}</div>
          )
        ) : (
          <div className="article-no-content">
            <p>No content available.</p>
          </div>
        )}
        <div className="shared-article-footer">
          Shared via <a href="https://eizel.co">Eizel</a> &middot;{' '}
          <a href="https://eizel.co">Sign up free</a>
        </div>
      </main>
    </>
  )
}
